import type {
  ApiResponse,
  AuthLoginResponse,
  AuthMeResponse,
  BannerSlide,
  Categoria,
  CategoriaProveedor,
  ClientePerfil,
  ConfirmarCorreoResponse,
  VerificarCorreoResponse,
  Kit,
  OrigenProveedor,
  PedidoDetalle,
  PrepararCuentaResponse,
  Producto,
  ProductoProveedor,
  PropositoAcceso,
} from '@/types';
import { getAuthToken } from '@/lib/authToken';

const API_BASE =
  import.meta.env.VITE_API_URL ??
  'https://sistema.grupoalarcom.mx/index.php/api/ecommerce';

function buildHeaders(extra?: HeadersInit, token?: string | null): Headers {
  const headers = new Headers(extra);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const authToken = token ?? getAuthToken();
  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
    headers.set('X-Auth-Token', authToken);
  }
  return headers;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const { headers: extraHeaders, ...rest } = options ?? {};
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: buildHeaders(extraHeaders),
  });

  const json = (await res.json()) as ApiResponse<T>;

  if (!res.ok || !json.success) {
    throw new Error(json.message ?? 'Error en la solicitud');
  }

  return json.data as T;
}

export const api = {
  health: () => request<{ status: string; version: string; cuentas_cliente?: boolean }>('/health'),

  authLogin: (usuario: string, clave: string) =>
    request<AuthLoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ usuario, clave }),
    }),

  authLogout: (token: string) =>
    request<null>('/auth/logout', {
      method: 'POST',
      headers: buildHeaders(undefined, token),
    }),

  authMe: (token?: string) =>
    request<AuthMeResponse>('/auth/me', {
      headers: buildHeaders(undefined, token),
    }),

  authPedido: (folio: string) => request<PedidoDetalle>(`/auth/pedidos/${encodeURIComponent(folio)}`),

  authVerificarCorreo: (correo: string) =>
    request<VerificarCorreoResponse>('/auth/verificar-correo', {
      method: 'POST',
      body: JSON.stringify({ correo }),
    }),

  authPrepararCuenta: (correo: string, proposito: PropositoAcceso) =>
    request<PrepararCuentaResponse>('/auth/preparar-cuenta', {
      method: 'POST',
      body: JSON.stringify({ correo, proposito }),
    }),

  authConfirmarCorreo: (correo: string, confirmar: boolean, proposito: PropositoAcceso) =>
    request<ConfirmarCorreoResponse>('/auth/confirmar-correo', {
      method: 'POST',
      body: JSON.stringify({ correo, confirmar, proposito }),
    }),

  authRestablecerClave: (
    correo: string,
    codigo: string,
    clave: string,
    proposito: PropositoAcceso,
  ) =>
    request<AuthLoginResponse>('/auth/restablecer-clave', {
      method: 'POST',
      body: JSON.stringify({ correo, codigo, clave, proposito }),
    }),

  productos: (params?: {
    categoria?: string;
    q?: string;
    orden?: string;
    solo_stock?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.categoria) qs.set('categoria', params.categoria);
    if (params?.q) qs.set('q', params.q);
    if (params?.orden) qs.set('orden', params.orden);
    if (params?.solo_stock) qs.set('solo_stock', '1');
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{
      items: Producto[];
      total: number;
      page?: number;
      pages?: number;
      limit?: number;
      orden?: string;
    }>(`/productos${query ? `?${query}` : ''}`);
  },

  producto: (id: number | string) => request<Producto>(`/productos/${encodeURIComponent(String(id))}`),

  kits: () => request<Kit[]>('/kits'),

  kit: (slug: string) => request<Kit>(`/kits/${slug}`),

  categorias: () => request<Categoria[]>('/categorias'),

  banners: () => request<BannerSlide[]>('/banners'),

  crearPedido: (payload: Record<string, unknown>) =>
    request<{ id_venta: number; folio: string }>('/pedidos', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  openpayConfig: () =>
    request<{
      enabled: boolean;
      merchantId: string;
      publicKey: string;
      sandbox: boolean;
      currency: string;
    }>('/openpay/config'),

  openpayCharge: (payload: Record<string, unknown>) =>
    request<{
      status: 'completed' | 'charge_pending';
      charge_id: string;
      folio?: string;
      id_venta?: number;
      redirect_url?: string;
      amount?: number;
    }>('/openpay/charge', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  openpayConfirm: (chargeId: string) =>
    request<{ id_venta: number; folio: string }>('/openpay/confirm', {
      method: 'POST',
      body: JSON.stringify({ charge_id: chargeId }),
    }),

  proveedores: () =>
    request<{
      items: Array<{
        codigo: OrigenProveedor;
        nombre: string;
        activo: boolean;
        origen: string;
        proximamente?: boolean;
      }>;
    }>('/proveedores'),

  proveedorHealth: (proveedor: OrigenProveedor) =>
    request<{ proveedor: string; status: string; configured: boolean }>(
      `/proveedores/${proveedor}/health`,
    ),

  proveedorCategorias: (
    proveedor: OrigenProveedor,
    params?: { formato?: 'arbol' | 'plano'; include_empty?: boolean },
  ) => {
    const qs = new URLSearchParams();
    if (params?.formato) qs.set('formato', params.formato);
    if (params?.include_empty) qs.set('include_empty', '1');
    const query = qs.toString();
    return request<{
      proveedor: OrigenProveedor;
      formato: string;
      items: CategoriaProveedor[];
      total: number;
    }>(`/proveedores/${proveedor}/categorias${query ? `?${query}` : ''}`);
  },

  proveedorProductos: (
    proveedor: OrigenProveedor,
    params?: {
      page?: number;
      per_page?: number;
      category_id?: string;
      q?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.per_page) qs.set('per_page', String(params.per_page));
    if (params?.category_id) qs.set('category_id', params.category_id);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    return request<{
      proveedor: OrigenProveedor;
      items: ProductoProveedor[];
      page: number;
      per_page: number;
      total: number;
      last_page?: number | null;
    }>(`/proveedores/${proveedor}/productos${query ? `?${query}` : ''}`);
  },

  proveedorProducto: (
    proveedor: OrigenProveedor,
    id: string,
    byModel = false,
  ) => {
    const qs = byModel ? '?by_model=1' : '';
    return request<ProductoProveedor>(
      `/proveedores/${proveedor}/productos/${encodeURIComponent(id)}${qs}`,
    );
  },
};

export function clienteToCheckoutForm(c: ClientePerfil) {
  return {
    nombre: c.nombre ?? '',
    email: c.email ?? '',
    telefono: c.telefono ?? '',
    calle: c.calle ?? '',
    numero: c.numero ?? '',
    colonia: c.colonia ?? '',
    municipio: c.municipio ?? '',
    estado: c.estado ?? '',
    codigo_postal: c.codigo_postal ?? '',
    requiere_factura: c.tiene_facturacion,
    rfc_fiscal: c.rfc_fiscal ?? '',
    razon_social_fiscal: c.razon_social_fiscal ?? '',
    uso_cfdi: c.uso_cfdi ?? '',
    regimen_fiscal: c.regimen_fiscal ?? '',
    codigo_postal_fiscal: c.codigo_postal_fiscal ?? c.codigo_postal ?? '',
  };
}

export function formatMXN(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
}

export function bannerImageUrl(imagen?: string | null): string {
  return productImageUrl(imagen);
}

export function productImageUrl(imagen?: string | null): string {
  if (!imagen) {
    return '/placeholder-product.svg';
  }
  if (imagen.startsWith('http')) {
    return imagen;
  }
  const base =
    import.meta.env.VITE_UPLOADS_URL ??
    'https://sistema.grupoalarcom.mx/uploads/ecommerce/';
  return `${base.replace(/\/?$/, '/')}${imagen.replace(/^\//, '')}`;
}
